import { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Space, message } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { getToken, setToken, removeToken } from "../utils/api";
import { useLocale } from "../contexts/LocaleContext";

interface TokenSettingsProps {
  onTokenChange?: () => void;
}

export default function TokenSettings({ onTokenChange }: TokenSettingsProps) {
  const { t } = useLocale();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [currentToken, setCurrentToken] = useState<string | null>(null);

  useEffect(() => {
    const token = getToken();
    setCurrentToken(token);
    if (token) {
      form.setFieldsValue({ token });
    }
  }, [form]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    form.setFieldsValue({ token: currentToken || "" });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setToken(values.token.trim());
      setCurrentToken(values.token.trim());
      setIsModalOpen(false);
      message.success(t.token.saveSuccess);
      onTokenChange?.();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t.token.deleteTitle,
      content: t.token.deleteConfirm,
      okText: t.common.confirm,
      cancelText: t.common.cancel,
      onOk: () => {
        removeToken();
        setCurrentToken(null);
        form.resetFields();
        message.success(t.token.deleteSuccess);
        onTokenChange?.();
      },
    });
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-text mb-1">{t.token.title}</h3>
            {currentToken ? (
              <p className="text-xs font-mono" style={{ color: "var(--text-secondary)" }}>{currentToken.substring(0, 20)}...</p>
            ) : (
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{t.token.notSet}</p>
            )}
          </div>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleOpenModal}>
              {t.token.edit}
            </Button>
            {currentToken && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                {t.token.delete}
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Modal
        title={t.token.title}
        open={isModalOpen}
        onOk={handleSave}
        onCancel={() => setIsModalOpen(false)}
        okText={t.common.save}
        cancelText={t.common.cancel}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="token"
            label="Master Key"
            rules={[{ required: true, message: t.token.placeholder }]}
          >
            <Input.Password placeholder={t.token.placeholder} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
